import { hexToWorld } from '../hex.ts';
import { dist2, idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, LitterRules, LitterState, NevState, SendaNpc } from '../types.ts';
import { attackHero, stepToward } from './enemies.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { frostTrace } from './nieve-layout.ts';
import { sendaCue } from './senda.ts';

/**
 * The cleric's last road, chapter 3 (docs/sendas/clerigo.md, «La nieve»; grounds in battle/nieve-layout.ts, numbers in
 * content CAMINO.cold, col, frost, corral). The stretcher's own rule (battle/litter.ts) carries it; this adds what each
 * act does on top, through the hooks litter.ts calls (`nevTick`, `nevHold`, `nevOrder`, `nevMarkAt`, `nevBusy`,
 * `nevResult`, `nevGoal`), and two more: `nevAloft` (the ash watcher up in the storm: nothing reaches it) and
 * `nevCarryBp` (a brazier in the hero's hands slows it).
 *
 * - Tessa's cold, in all three: a bar that always drops (faster with the stretcher still), up again by a lit brazier
 *   near her; at the bottom she takes a blow now and then.
 * - The white col (`col`): the stretcher carried up the col; the chapels' braziers lit by the hero standing by them;
 *   wolves out of the snow for the stretcher; Joran and his apprentices behind with their own fire, never helping.
 * - The frozen mirrors (`frost`, §4): the lantern fixed by the entrance, five mirrors under frost (a frozen one lets the
 *   beam through straight and can't be turned), six bases. Three braziers at the pile: one carried at a time (slower),
 *   set on a base and lit; taken up again. A lit brazier thaws the mirrors of its base after a moment; with none by it a
 *   mirror frosts over again a while after (nothing stays wrong for good). The beam lights the door: it opens. Tessa's
 *   cold drops with no brazier by the stretcher; wolves come for it.
 * - The night in the corral (`corral`, the test): seven minutes on the clock. Moths of sleep for the lit braziers (one
 *   at a brazier long enough puts it out); wolves through the gaps; the ash watcher up in the storm dives on the weakest
 *   in the corral, the shadow of its wings on the floor first: whoever stands in the shadow when it lands takes it (the
 *   hero, for someone else), and the lit brazier nearest to whom it struck goes out. One of the family struck with no
 *   lit brazier near is left frozen in the straw until the hero gets them up. It can only be struck while it is down.
 *
 * Cost: a handful of nightmares, three to six braziers and five mirrors looked at once a tick; the beam worked out again
 * only when a mirror thaws, frosts or turns.
 */

/** The marks: a brazier or a base `NEV_MARK + i`, the braziers' pile, a mirror `NEV_MIRROR + i`. */
export const NEV_MARK = 20;
export const NEV_PILE = 39;
export const NEV_MIRROR = 40;
const NEVER = 0x7fffffff;
const FAMILY = ['kidA', 'kidB', 'pietMother'];

const person = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);
const scaled = (s: BattleState, e: Enemy): Enemy => {
  if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  return e;
};

const fresh = (r: LitterRules): NevState => {
  const S = r.snow;
  const F = S?.frost;
  const C = S?.corral;
  return {
    cold: S ? (r.kind === 'col' ? S.cold.full : idiv(S.cold.full * 8, 10)) : 0,
    bite: 0,
    lit: F ? F.bases.map(() => 0) : (S?.braziers ?? []).map((b) => (b.lit ? 1 : 0)),
    pile: F ? 3 : 0,
    carry: 0,
    warmT: F ? F.mirrors.map(() => 0) : [],
    coolT: F ? F.mirrors.map(() => 0) : [],
    ice: F ? F.mirrors.map(() => 1) : [],
    wolf: S?.wolf.first ?? NEVER,
    moth: C?.moth.first ?? NEVER,
    snuff: [],
    boss: 0,
    dive: C?.watcher.first ?? NEVER,
    at: -1,
    ax: 0,
    ay: 0,
    who: '',
    down: 0,
    frozen: [],
    thawT: FAMILY.map(() => 0),
    struck: 0,
    took: 0,
  };
};

/** Where a brazier (or a base) is. */
export const nevBrazierAt = (r: LitterRules, i: number): { x: number; y: number } | null => {
  const S = r.snow;
  if (!S) return null;
  const b = S.frost ? S.frost.bases[i] : undefined;
  if (b) return hexToWorld(b.q, b.r);
  const k = S.braziers[i];
  return k ? { x: k.x, y: k.y } : null;
};

const braziers = (r: LitterRules): number => (r.snow?.frost ? r.snow.frost.bases.length : (r.snow?.braziers.length ?? 0));

/** Some lit brazier within `d` of a point (its index), or −1. */
const litNear = (r: LitterRules, n: NevState, x: number, y: number, d: number): number => {
  let best = -1;
  let bd = NEVER;
  for (let i = 0; i < n.lit.length; i++) {
    if (!n.lit[i]) continue;
    const p = nevBrazierAt(r, i);
    if (!p) continue;
    const dd = dist2(x, y, p.x, p.y);
    if (dd <= d * d && dd < bd) {
      bd = dd;
      best = i;
    }
  }
  return best;
};

/** A blow on one of the stretcher's three (Tessa's in ten-thousandths, as litter.ts settles them). */
const blowOn = (s: BattleState, w: LitterState, n: SendaNpc, blows: number, cue: (c: string) => void): void => {
  if (n.hp <= 0 || blows <= 0) return;
  if (n.id === 'tessa') {
    w.acc += blows * 10000;
    while (w.acc >= 10000 && n.hp > 0) {
      w.acc -= 10000;
      n.hp--;
      ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
    }
    return;
  }
  n.hp = imax(0, n.hp - blows);
  ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
  if (n.hp <= 0 && (n.id === 'piet' || n.id === 'ludo')) {
    ev(s, { t: 'senda', kind: 'set', npc: n.id, x: w.x, y: w.y, n: n.id === 'piet' ? 0 : 1 });
    cue('down1');
  }
};

/* ---------- the hooks litter.ts calls ---------- */

/** Sends the hero to a brazier, a base, the pile or a mirror. */
export const nevOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  if (!r?.snow || !w) return;
  const F = r.snow.frost;
  let p: { x: number; y: number } | null = null;
  if (at === NEV_PILE && F) p = F.pile;
  else if (at >= NEV_MIRROR && F) p = F.mirrors[at - NEV_MIRROR]?.stand ?? null;
  else if (at >= NEV_MARK && at < NEV_MARK + braziers(r)) {
    const b = nevBrazierAt(r, at - NEV_MARK);
    const st = r.snow.braziers[at - NEV_MARK]?.stand;
    // (Beside it: on the col, its cell nearest the way; elsewhere, toward the stretcher.)
    if (st) p = { ...st };
    else if (b) p = { x: b.x + (b.x > w.x ? -500 : 500), y: b.y + 250 };
  }
  if (!p || (F && w.lit >= 0)) return;
  w.job = at;
  w.held = 0;
  orderMove(s, h, p.x, p.y);
  h.target = 0;
  h.forced = false;
};

/** The point of a mark (a brazier, a base, the pile, a mirror). */
export const nevMarkAt = (s: BattleState, at: number): { x: number; y: number } | null => {
  const r = s.rules?.senda?.litter;
  if (!r?.snow) return null;
  const F = r.snow.frost;
  if (at === NEV_PILE) return F ? { ...F.pile } : null;
  if (at >= NEV_MIRROR) {
    const m = F?.mirrors[at - NEV_MIRROR];
    return m ? hexToWorld(m.q, m.r) : null;
  }
  return nevBrazierAt(r, at - NEV_MARK);
};

/** The hero's pace with a brazier in its hands (ten-thousandths). */
export const nevCarryBp = (s: BattleState): number => {
  const n = s.senda?.litter?.nev;
  const F = s.rules?.senda?.litter?.snow?.frost;
  return n && F && n.carry ? F.carryBp : 10000;
};

/** The ash watcher up in the storm: nothing reaches it. */
export const nevAloft = (s: BattleState, e: Enemy): boolean => {
  const n = s.senda?.litter?.nev;
  return !!n && n.boss === e.id && e.hp > 0 && s.waveT >= n.down;
};

/** Nightmares at their own work: the moths at a brazier, the watcher. */
export const nevBusy = (s: BattleState, e: Enemy): boolean => {
  const n = s.senda?.litter?.nev;
  return !!n && (e.id === n.boss || e.kind === 'moth');
};

/* ---------- the cold and the braziers ---------- */

const coldTick = (s: BattleState, r: LitterRules, w: LitterState, n: NevState, cue: (c: string) => void): void => {
  const S = r.snow;
  const t = person(s, 'tessa');
  if (!S || !t || t.hp <= 0 || (r.kind === 'col' && w.done >= 0)) return;
  const C = S.cold;
  if (litNear(r, n, w.x, w.y, C.warmR) >= 0) n.cold = imin(C.full, n.cold + C.warm);
  else n.cold = imax(0, n.cold - (S.frost ? S.frost.drop : S.corral ? S.corral.drop : w.moving ? C.drop : C.still));
  if (n.cold * 10 < C.full * 4) cue(S.frost ? 'shiver' : 'blue');
  if (n.cold > 0) {
    n.bite = 0;
    return;
  }
  if (++n.bite < C.bite) return;
  n.bite = 0;
  blowOn(s, w, t, 1, cue);
  ev(s, { t: 'senda', kind: 'chill', npc: 'tessa', x: t.x, y: t.y });
};

/** The brazier, base, pile or mirror the hero was sent to, worked by standing still by it. */
const work = (s: BattleState, r: LitterRules, w: LitterState, n: NevState, h: Hero | undefined, cue: (c: string) => void): void => {
  const S = r.snow;
  if (!S || w.job < 0 || !h) return;
  const F = S.frost;
  const at = w.job;
  const p = nevMarkAt(s, at);
  if (!p) {
    w.job = -1;
    return;
  }
  const reach = at >= NEV_MIRROR && F ? F.reach + 300 : S.cold.reach;
  if (h.moving || !withinRange(h.x, h.y, p.x, p.y, reach)) {
    w.held = 0;
    return;
  }
  // What it takes: a mirror's turn, taking a brazier up, lighting one.
  let need = S.cold.light;
  if (at === NEV_PILE) need = F?.take ?? 12;
  else if (at >= NEV_MIRROR) {
    const i = at - NEV_MIRROR;
    if (n.ice[i]) {
      // Frozen: it doesn't turn (Aud's «first the fire»).
      w.job = -1;
      ev(s, { t: 'senda', kind: 'iced', npc: '', x: p.x, y: p.y, n: i });
      cue('iced');
      return;
    }
    need = F?.stand ?? 12;
  } else if (F) {
    const b = at - NEV_MARK;
    if (n.lit[b]) need = F.take;
    else if (!n.carry) {
      w.job = -1;
      return;
    }
  } else if (n.lit[at - NEV_MARK]) {
    w.job = -1;
    return;
  }
  if (++w.held < need) return;
  w.held = 0;
  w.job = -1;
  if (at === NEV_PILE) {
    if (n.pile > 0 && !n.carry) {
      n.pile--;
      n.carry = 1;
      ev(s, { t: 'senda', kind: 'take', npc: '', x: p.x, y: p.y, n: -1 });
    }
    return;
  }
  if (at >= NEV_MIRROR) {
    const i = at - NEV_MIRROR;
    w.turns[i] = ((w.turns[i] ?? 0) + 1) % 3;
    ev(s, { t: 'senda', kind: 'mirror', npc: '', x: p.x, y: p.y, n: i });
    rebeam(s, r, w, n, cue);
    return;
  }
  const b = at - NEV_MARK;
  if (F && n.lit[b]) {
    if (n.carry) {
      // (Hands full: the one in them goes back to the pile first.)
      n.pile++;
    }
    n.lit[b] = 0;
    n.carry = 1;
    ev(s, { t: 'senda', kind: 'take', npc: '', x: p.x, y: p.y, n: b });
    return;
  }
  if (F) n.carry = 0;
  n.lit[b] = 1;
  ev(s, { t: 'senda', kind: 'kindle', npc: '', x: p.x, y: p.y, n: b });
  const k = n.lit.reduce((a, v) => a + v, 0);
  cue(`lit${imin(9, k)}`);
};

/* ---------- the white col ---------- */

const colTick = (s: BattleState, r: LitterRules, w: LitterState, n: NevState, cue: (c: string) => void): void => {
  const S = r.snow;
  if (!S) return;
  // Joran and his apprentices, a way back: his lantern, theirs beside him.
  const back = imax(0, w.seg - S.behind);
  const p = r.way[back] as { x: number; y: number };
  const go = w.seg - S.behind > 0 && w.moving;
  const band: [string, number, number][] = [
    ['joran', 0, 0],
    ['apprenticeA', -700, 500],
    ['apprenticeB', 700, 450],
  ];
  for (const [id, dx, dy] of band) {
    const m = person(s, id);
    if (!m) continue;
    m.x = p.x + dx;
    m.y = p.y + dy + 300;
    m.facing = w.facing;
    m.moving = go;
  }
  wolves(s, r, w, n, cue);
};

/** Wolves out of the snow: the col's dens ahead of the stretcher, the terrace's and the corral's in turn. */
const wolves = (s: BattleState, r: LitterRules, w: LitterState, n: NevState, cue: (c: string) => void): void => {
  const S = r.snow;
  if (!S || s.waveT < n.wolf || (r.kind === 'col' && w.done >= 0) || (S.frost && w.lit >= 0)) return;
  n.wolf = s.waveT + S.wolf.every + rngRange(s.rng, -12, 12);
  let pick: { x: number; y: number } | null = null;
  if (r.kind === 'col') {
    let bd = NEVER;
    for (const d of S.dens) {
      // (Ahead of it, a little: they come down at it out of the storm.)
      const ahead = w.y - d.y;
      if (ahead < 1500 || ahead > 6000) continue;
      const dd = dist2(d.x, d.y, w.x, w.y);
      if (dd < bd) {
        bd = dd;
        pick = d;
      }
    }
  } else pick = S.dens[rngInt(s.rng, S.dens.length)] ?? null;
  if (!pick) return;
  const k = rngRange(s.rng, S.wolf.n[0], S.wolf.n[1]) + (S.wolf.grow > 0 ? idiv(s.waveT, S.wolf.grow) : 0);
  for (let i = 0; i < k; i++) scaled(s, spawnEnemy(s, 'wolf', 1, false, 0, pick.x + i * 350, pick.y + rngRange(s.rng, -300, 300)));
  cue('wolf');
};

/* ---------- the frozen mirrors ---------- */

/** The beam worked out again (a mirror thawed, frosted or turned): its corners for the drawing, the door lit. */
const rebeam = (s: BattleState, r: LitterRules, w: LitterState, n: NevState, cue: (c: string) => void): void => {
  const F = r.snow?.frost;
  if (!F) return;
  const b = frostTrace({ lantern: F.lantern, door: F.door, mirrors: F.mirrors, walls: F.walls, radius: F.radius }, w.turns, n.ice);
  w.beam = [];
  for (const p of b.pts) {
    const c = hexToWorld(p.q, p.r);
    w.beam.push(c.x, c.y);
  }
  w.hits = b.by.length;
  if (b.lit && w.lit < 0) {
    w.lit = s.tick;
    const c = hexToWorld(F.door.q, F.door.r);
    ev(s, { t: 'senda', kind: 'lamp', npc: '', x: c.x, y: c.y });
    cue('lit');
  } else if (!b.lit && b.by.length >= 2) cue('stray');
};

const frostTick = (s: BattleState, r: LitterRules, w: LitterState, n: NevState, h: Hero | undefined, cue: (c: string) => void): void => {
  const F = r.snow?.frost;
  if (!F) return;
  if (w.turns.length !== F.mirrors.length) {
    w.turns = [...F.start];
    rebeam(s, r, w, n, cue);
  }
  if (w.lit >= 0) return;
  // The frost: thawed by a lit brazier on a base that reaches it, frosted over again a while after it has none.
  let changed = false;
  for (let i = 0; i < F.mirrors.length; i++) {
    let warm = false;
    for (let b = 0; b < F.bases.length && !warm; b++) if (n.lit[b] && (F.thaws[b] as number[]).includes(i)) warm = true;
    if (warm) {
      n.coolT[i] = 0;
      if (n.ice[i]) {
        n.warmT[i] = (n.warmT[i] ?? 0) + 1;
        if ((n.warmT[i] as number) >= F.thaw) {
          n.ice[i] = 0;
          n.warmT[i] = 0;
          changed = true;
          const m = F.mirrors[i] as { q: number; r: number };
          const c = hexToWorld(m.q, m.r);
          ev(s, { t: 'senda', kind: 'thaw', npc: '', x: c.x, y: c.y, n: i });
          cue('thaw1');
        }
      }
    } else {
      n.warmT[i] = 0;
      if (!n.ice[i]) {
        n.coolT[i] = (n.coolT[i] ?? 0) + 1;
        if ((n.coolT[i] as number) >= F.refreeze) {
          n.ice[i] = 1;
          n.coolT[i] = 0;
          changed = true;
          const m = F.mirrors[i] as { q: number; r: number };
          const c = hexToWorld(m.q, m.r);
          ev(s, { t: 'senda', kind: 'frost', npc: '', x: c.x, y: c.y, n: i });
        }
      }
    }
  }
  if (changed) rebeam(s, r, w, n, cue);
  wolves(s, r, w, n, cue);
  if (h && s.tick % 12 === 0) {
    const c = hexToWorld(F.lantern.q, F.lantern.r);
    if (withinRange(h.x, h.y, c.x, c.y, 3200)) cue('inscription');
  }
  // The beam burns what stands in it.
  if (s.tick % 12 === 0 && w.beam.length >= 4)
    for (const e of s.enemies) {
      if (e.hp <= 0) continue;
      for (let i = 0; i + 3 < w.beam.length; i += 2) {
        if (segD2(e.x, e.y, w.beam[i] as number, w.beam[i + 1] as number, w.beam[i + 2] as number, w.beam[i + 3] as number) <= 450 * 450) {
          damageEnemy(s, e, F.burn, { pid: '', type: 'holy' });
          break;
        }
      }
    }
};

const segD2 = (px: number, py: number, ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? idiv(((px - ax) * dx + (py - ay) * dy) * 1000, len2) : 0;
  t = t < 0 ? 0 : t > 1000 ? 1000 : t;
  return dist2(px, py, ax + idiv(dx * t, 1000), ay + idiv(dy * t, 1000));
};

/* ---------- the night in the corral ---------- */

/** Who the watcher dives on: the weakest in the corral (the least life left by its share), the children first. */
const weakest = (s: BattleState, n: NevState, h: Hero | undefined): { id: string; x: number; y: number } | null => {
  let best: { id: string; x: number; y: number } | null = null;
  let bk = NEVER;
  for (const id of [...FAMILY, 'tessa', 'piet', 'ludo']) {
    const m = person(s, id);
    if (!m || m.hp <= 0 || n.frozen.includes(id)) continue;
    const k = idiv(m.hp * 10000, imax(1, m.maxHp));
    if (k < bk) {
      bk = k;
      best = { id, x: m.x, y: m.y };
    }
  }
  if (h && h.stats.maxHp > 0) {
    const k = idiv(h.hp * 10000, h.stats.maxHp);
    if (k < bk) best = { id: '', x: h.x, y: h.y };
  }
  return best;
};

const corralTick = (s: BattleState, r: LitterRules, w: LitterState, n: NevState, h: Hero | undefined, cue: (c: string) => void): void => {
  const S = r.snow;
  const C = S?.corral;
  if (!S || !C) return;
  // The family where they sleep in the straw.
  for (const f of C.family) {
    const m = person(s, f.id);
    if (!m) continue;
    m.x = f.x;
    m.y = f.y;
    m.moving = false;
    m.facing = 256;
  }
  if (s.waveT >= C.night) return;
  if (!n.boss) {
    const p = C.sky[0] as { x: number; y: number };
    const e = spawnEnemy(s, 'ashWatcher', 1, false, 0, p.x, p.y, C.watcher.hp);
    n.boss = e.id;
  }
  const b = s.enemies.find((e) => e.id === n.boss && e.hp > 0);
  if (b) b.stun = 0;
  else if (n.at !== -2) {
    // Down for good: no more dives.
    n.at = -2;
    cue('fell');
  }
  // Its dive: the shadow first, on the weakest; then it lands where the shadow is.
  if (b && n.at === -1 && s.waveT >= n.dive - C.watcher.warn) {
    const t = weakest(s, n, h);
    if (t) {
      n.at = s.waveT + C.watcher.warn;
      n.ax = t.x;
      n.ay = t.y;
      n.who = t.id;
      ev(s, { t: 'senda', kind: 'diveWarn', npc: t.id, x: t.x, y: t.y, n: C.watcher.warn });
      cue('shadow1');
    }
  }
  if (b && n.at >= 0 && s.waveT >= n.at) land(s, r, w, n, b, h, cue);
  // The frozen: the hero by one gets them up.
  FAMILY.forEach((id, i) => {
    const m = person(s, id);
    if (!m || !n.frozen.includes(id)) {
      n.thawT[i] = 0;
      return;
    }
    if (!h || !withinRange(h.x, h.y, m.x, m.y, r.liftReach)) {
      n.thawT[i] = 0;
      return;
    }
    n.thawT[i] = (n.thawT[i] ?? 0) + 1;
    if ((n.thawT[i] as number) < r.lift) return;
    n.thawT[i] = 0;
    n.frozen = n.frozen.filter((x) => x !== id);
    m.hp = m.maxHp;
    ev(s, { t: 'senda', kind: 'lifted', npc: id, x: m.x, y: m.y });
  });
  // Moths from the storm.
  if (s.waveT >= n.moth) {
    n.moth = s.waveT + C.moth.every;
    for (let k = 0; k < C.moth.n; k++) {
      const p = C.sky[rngInt(s.rng, C.sky.length)] as { x: number; y: number };
      scaled(s, spawnEnemy(s, 'moth', 1, false, 0, p.x + k * 300, p.y + rngRange(s.rng, -300, 300)));
    }
  }
  if (n.snuff.length) n.snuff = n.snuff.filter((x) => s.enemies.some((e) => e.id === x.id && e.hp > 0) && n.lit[x.b]);
  for (const x of n.snuff) {
    if (s.tick - x.t < C.moth.snuff || !n.lit[x.b]) continue;
    n.lit[x.b] = 0;
    const p = nevBrazierAt(r, x.b);
    if (p) ev(s, { t: 'senda', kind: 'snuffed', npc: '', x: p.x, y: p.y, n: x.b });
    // (It burns in the brazier it put out.)
    const e = s.enemies.find((o) => o.id === x.id && o.hp > 0);
    if (e) damageEnemy(s, e, e.hp, { pid: '', type: 'fire' });
  }
  wolves(s, r, w, n, cue);
  if (s.waveT * 2 >= C.night && s.waveT * 2 < C.night + 12) cue('half');
};

/** The watcher lands where its shadow is: who stands in it takes it; the lit brazier nearest them goes out. */
const land = (s: BattleState, r: LitterRules, w: LitterState, n: NevState, b: Enemy, h: Hero | undefined, cue: (c: string) => void): void => {
  const C = r.snow?.corral;
  if (!C) return;
  const V = C.watcher;
  b.x = n.ax;
  b.y = n.ay;
  b.anim = 'attack';
  b.animT = 0;
  n.at = -1;
  n.dive = s.waveT + V.every;
  n.struck++;
  ev(s, { t: 'senda', kind: 'dive', npc: n.who, x: n.ax, y: n.ay });
  // The hero in the shadow takes it, for whoever it was meant for.
  if (h && withinRange(h.x, h.y, n.ax, n.ay, V.r)) {
    damageHero(s, h, mulBp(h.stats.maxHp, V.heroBp), b, 'shadow', true);
    n.down = s.waveT + (n.who ? V.downHeld : V.down);
    if (n.who) {
      n.took++;
      cue('took');
    }
    return;
  }
  n.down = s.waveT + V.down;
  const m = n.who ? person(s, n.who) : undefined;
  if (!m || !withinRange(m.x, m.y, n.ax, n.ay, V.r)) return;
  const warm = litNear(r, n, m.x, m.y, C.freezeR) >= 0;
  // It steals the warmth: the lit brazier nearest to whom it struck.
  const out = litNear(r, n, m.x, m.y, 0x3fff);
  if (out >= 0) {
    n.lit[out] = 0;
    const p = nevBrazierAt(r, out);
    if (p) ev(s, { t: 'senda', kind: 'snuffed', npc: '', x: p.x, y: p.y, n: out });
  }
  if (FAMILY.includes(m.id)) {
    m.hp = imax(0, m.hp - 1);
    if (!warm || m.hp <= 0) {
      if (!n.frozen.includes(m.id)) n.frozen.push(m.id);
      m.hp = 0;
      ev(s, { t: 'senda', kind: 'frozen', npc: m.id, x: m.x, y: m.y });
      cue('frozen1');
    } else ev(s, { t: 'senda', kind: 'hit', npc: m.id, x: m.x, y: m.y });
    return;
  }
  blowOn(s, w, m, V.blows, cue);
};

/* ---------- the hooks ---------- */

export const nevTick = (s: BattleState, r: LitterRules, w: LitterState, h: Hero | undefined, cue: (c: string) => void): void => {
  if (!r.snow) return;
  const n = (w.nev ??= fresh(r));
  work(s, r, w, n, h, cue);
  coldTick(s, r, w, n, cue);
  if (r.kind === 'col') colTick(s, r, w, n, cue);
  else if (r.kind === 'frost') frostTick(s, r, w, n, h, cue);
  else corralTick(s, r, w, n, h, cue);
};

/** Its own nightmares (true: handled here): the moths at the braziers, the watcher up in the storm or down. */
export const nevHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const n = w?.nev;
  const C = r?.snow?.corral;
  if (!r || !w || !n || !C) return false;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (e.id === n.boss) {
    e.stun = 0;
    if (s.waveT < n.down) {
      // Down where it landed: its wings round whoever is by it.
      e.anim = s.waveT + 6 >= n.down ? 'walk' : 'idle';
      return true;
    }
    // Up in the storm: round the corral, over the one it means to dive on once warned.
    e.anim = 'walk';
    if (n.at >= 0) stepToward(e, n.ax, n.ay - 1600, imax(px(e), 260));
    else {
      const k = idiv(s.waveT, 60) % C.sky.length;
      const p = C.sky[k] as { x: number; y: number };
      stepToward(e, idiv(p.x * 7, 10), idiv(p.y * 7, 10), px(e));
    }
    return true;
  }
  if (e.kind !== 'moth') return false;
  // A moth: to the nearest lit brazier, and on it until it goes out; with none lit, at the hero.
  const at = n.snuff.find((x) => x.id === e.id);
  if (at && n.lit[at.b]) {
    const p = nevBrazierAt(r, at.b);
    if (p) {
      e.x = p.x + ((e.id * 97) % 300) - 150;
      e.y = p.y - 200 + ((e.id * 53) % 200);
    }
    e.anim = 'attack';
    return true;
  }
  const b = litNear(r, n, e.x, e.y, 0x3fff);
  const p = b >= 0 ? nevBrazierAt(r, b) : null;
  if (p) {
    e.anim = 'walk';
    if (stepToward(e, p.x, p.y, px(e)) || withinRange(e.x, e.y, p.x, p.y, 350)) {
      n.snuff.push({ id: e.id, b, t: s.tick });
      ev(s, { t: 'senda', kind: 'perch', npc: '', x: p.x, y: p.y, n: b });
      sendaCue(s, 'moth');
    }
    return true;
  }
  if (!h) return true;
  if (withinRange(e.x, e.y, h.x, h.y, 500)) attackHero(s, e, h);
  else {
    e.anim = 'walk';
    stepToward(e, h.x, h.y, px(e));
  }
  return true;
};

/** How far along: the col's way (litter.ts), the door lit, the night gone by. */
export const nevGoal = (s: BattleState): { done: number; need: number } | null => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  if (!r?.snow || !w || r.kind === 'col') return null;
  if (r.snow.frost) return { done: w.lit >= 0 ? 1 : 0, need: 1 };
  const N = r.snow.corral?.night ?? 1;
  return { done: imin(N, s.waveT), need: N };
};

/** Won or lost by its own rule: the door open (the terrace), the night over or two of the family frozen (the corral). */
export const nevResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const n = w?.nev;
  if (!r?.snow || !w || !n) return null;
  const F = r.snow.frost;
  if (F) {
    if (w.lit >= 0 && s.tick >= w.lit + F.open) {
      if (s.tick === w.lit + F.open) {
        const c = hexToWorld(F.door.q, F.door.r);
        ev(s, { t: 'senda', kind: 'door', npc: '', x: c.x, y: c.y });
      }
      return 'won';
    }
    return null;
  }
  const C = r.snow.corral;
  if (C) {
    if (n.frozen.length >= 2) return 'lost';
    if (s.waveT >= C.night) {
      sendaCue(s, 'dawn');
      return 'won';
    }
  }
  return null;
};
